import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { teacherApi, type IncomingRequest } from '@/services/api/teacher'
import { teacherPriorityApi } from '@/services/api/teacherPriority'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { IncomingRequestCard } from '@/components/teacher-request/IncomingRequestCard'
import { StornoModal } from '@/components/teacher-request/StornoModal'
import { StornoRequestsPanel } from '@/components/teacher-request/StornoRequestsPanel'
import { getIncomingDisplayStatus } from '@/utils/statusHelpers'
import { RefreshCw } from 'lucide-react'
import { getHighlightParam, clearHighlightParam, getFocusParam } from '@/utils/radarNavigation'
import { todayLocalISO } from '@/lib/date-utils'

type TabType = 'all' | 'pending' | 'accepted' | 'declined' | 'not_cancelled' | 'cancelled'
type TimeFilter = 'all' | 'current' | 'past'

const STATUS_CHIPS: { label: string; value: TabType }[] = [
  { label: 'Nicht storniert', value: 'not_cancelled' },
  { label: 'Alle', value: 'all' },
  { label: 'Offen', value: 'pending' },
  { label: 'Angenommen', value: 'accepted' },
  { label: 'Abgelehnt', value: 'declined' },
  { label: 'Storniert', value: 'cancelled' },
]

export function TeacherIncomingRequestsPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  const [requests, setRequests] = useState<IncomingRequest[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [stornoTarget, setStornoTarget] = useState<IncomingRequest | null>(null)
  const [stornoPreselectedLessons, setStornoPreselectedLessons] = useState<string[]>([])
  const requestsRef = useRef<IncomingRequest[]>([])
  requestsRef.current = requests

  const [highlightedRequestId, setHighlightedRequestId] = useState<string | null>(null)
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const highlightProcessedRef = useRef(false)

  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const handleSendMessage = useCallback((requestId: string) => {
    navigate(`/teacher/chats?selected=${requestId}`)
  }, [navigate])

  const activeTab = (searchParams.get('tab') as TabType) || 'not_cancelled'
  const timeFilter = (searchParams.get('time') as TimeFilter) || 'current'

  const setActiveTab = useCallback((tab: TabType) => {
    setSearchParams({ tab, time: timeFilter })
  }, [setSearchParams, timeFilter])

  const setTimeFilter = useCallback((filter: TimeFilter) => {
    setSearchParams({ tab: activeTab, time: filter })
  }, [setSearchParams, activeTab])

  const fetchRequests = useCallback(async (isInitial = false) => {
    if (isInitial) {
      setIsLoading(true)
    } else {
      setIsRefreshing(true)
    }
    try {
      const data = await teacherApi.getIncomingRequests()
      setRequests(prev => {
        if (!isInitial && prev.length === data.requests.length) {
          const prevMap = new Map(prev.map(r => [r.id, r]))
          let changed = false
          const merged = data.requests.map(r => {
            const old = prevMap.get(r.id)
            if (old && JSON.stringify(old) === JSON.stringify(r)) return old
            changed = true
            return r
          })
          if (!changed && prev.length === merged.length) return prev
          return merged
        }
        return data.requests
      })
      setError(null)
    } catch (e: unknown) {
      const detail = (e as { detail?: string })?.detail || 'Anfragen konnten nicht geladen werden'
      setError(detail)
    } finally {
      setIsLoading(false)
      setIsRefreshing(false)
      if (isInitial) {
        setIsInitialLoad(false)
      }
    }
  }, [])

  useEffect(() => {
    fetchRequests(true)
  }, [fetchRequests])

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible') return
      const list = requestsRef.current
      const hasActive = list.some(r => {
        const ds = getIncomingDisplayStatus(r)
        return ds === 'in_progress' || ds === 'pending' || ds === 'timed_out'
      })
      if (hasActive) fetchRequests(false)
    }, 45000)
    return () => clearInterval(id)
  }, [fetchRequests])

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchRequests(false)
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [fetchRequests])

  const filteredRequests = useMemo(() => {
    let filtered = [...requests]

    if (activeTab !== 'all') {
      if (activeTab === 'not_cancelled') {
        filtered = filtered.filter(r => getIncomingDisplayStatus(r) !== 'cancelled')
      } else if (activeTab === 'pending') {
        filtered = filtered.filter(r => {
          const ds = getIncomingDisplayStatus(r)
          return ds === 'pending' || ds === 'in_progress' || ds === 'timed_out'
        })
      } else {
        filtered = filtered.filter(r => getIncomingDisplayStatus(r) === activeTab)
      }
    }

    const today = todayLocalISO()

    if (timeFilter === 'current') {
      filtered = filtered.filter(r => !r.date || r.date >= today)
    } else if (timeFilter === 'past') {
      filtered = filtered.filter(r => r.date && r.date < today)
    }

    filtered.sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0
      const dateB = b.date ? new Date(b.date).getTime() : 0
      if (dateA !== dateB) return dateB - dateA

      const aPrep = a.prep_summary?.has_open_prep_request ? 1 : 0
      const bPrep = b.prep_summary?.has_open_prep_request ? 1 : 0
      return bPrep - aPrep
    })

    return filtered
  }, [requests, activeTab, timeFilter])

  const counts = useMemo(() => {
    return {
      all: requests.length,
      pending: requests.filter(r => {
        const ds = getIncomingDisplayStatus(r)
        return ds === 'pending' || ds === 'in_progress' || ds === 'timed_out'
      }).length,
      accepted: requests.filter(r => getIncomingDisplayStatus(r) === 'accepted').length,
      declined: requests.filter(r => getIncomingDisplayStatus(r) === 'declined').length,
      not_cancelled: requests.filter(r => getIncomingDisplayStatus(r) !== 'cancelled').length,
      cancelled: requests.filter(r => getIncomingDisplayStatus(r) === 'cancelled').length,
    }
  }, [requests])

  const handleCancel = useCallback((request: IncomingRequest) => {
    setStornoPreselectedLessons([])
    setStornoTarget(request)
  }, [])

  const handleCancelLesson = useCallback((request: IncomingRequest, lessonId: string) => {
    setStornoPreselectedLessons([lessonId])
    setStornoTarget(request)
  }, [])

  const handleRequestPrep = useCallback(async (requestId: string, reason?: string, lessonRequestIds?: string[]) => {
    try {
      await teacherPriorityApi.requestPrep(requestId, {
        reason,
        lesson_request_ids: lessonRequestIds && lessonRequestIds.length > 0 ? lessonRequestIds : undefined,
      })
      await Promise.all([
        fetchRequests(false),
        queryClient.invalidateQueries({ queryKey: ['lesson-status', requestId] }),
        queryClient.invalidateQueries({ queryKey: ['prep-requests', requestId] }),
        queryClient.invalidateQueries({ queryKey: ['preps', requestId] }),
      ])
    } catch (e: unknown) {
      setError((e as { detail?: string })?.detail || 'Prep-Anforderung fehlgeschlagen')
    }
  }, [fetchRequests, queryClient])

  const handleResponded = useCallback(() => {
    return fetchRequests(false)
  }, [fetchRequests])

  const handleDismissHighlight = useCallback(() => {
    setHighlightedRequestId(null)
    clearHighlightParam()
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current)
    }
  }, [])

  useEffect(() => {
    const highlightParam = getHighlightParam()

    if (!highlightParam || highlightProcessedRef.current) return
    if (isLoading || isInitialLoad) return

    const isShared = !!document.referrer && !document.referrer.includes(window.location.origin)
    const cleanupDelay = isShared ? 3000 : 6000

    const request = requests.find(r => r.id === highlightParam)

    if (!request) {
      highlightProcessedRef.current = true
      setError(prev => prev || 'Diese Anfrage ist in «Anfragen an mich» nicht sichtbar. Sie ist abgelaufen, bereits erledigt oder gehört zu einem anderen Konto.')
      return
    }

    const displayStatus = getIncomingDisplayStatus(request)
    const isCancelled = displayStatus === 'cancelled'
    const isExpired = displayStatus === 'expired'

    if (isCancelled || isExpired) {
      clearHighlightParam()
      setError('Anfrage ist nicht mehr aktiv')
      return
    }

    const isVisibleInFilter = filteredRequests.some(r => r.id === highlightParam)
    let needsFilterChange = false

    if (!isVisibleInFilter) {
      const targetTab: TabType = displayStatus === 'pending' || displayStatus === 'in_progress'
        ? 'pending'
        : displayStatus === 'accepted'
          ? 'accepted'
          : displayStatus === 'declined'
            ? 'declined'
            : 'all'

      const today = todayLocalISO()
      const targetTime = (request.date && request.date < today && timeFilter === 'current') ? 'all' : timeFilter

      if (targetTab !== activeTab || targetTime !== timeFilter) {
        needsFilterChange = true
        setSearchParams({ tab: targetTab, time: targetTime })
      }

      if (needsFilterChange) {
        return
      }
    }

    highlightProcessedRef.current = true
    setHighlightedRequestId(highlightParam)

    const cardElement = document.getElementById(`request-card-${highlightParam}`)
    if (cardElement) {
      setTimeout(() => {
        cardElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        })
      }, 100)
    }

    // Magic-link arrival (focus=1): keep the highlight persistent so the teacher can
    // act on the card. Otherwise auto-clear after a short delay.
    if (!getFocusParam()) {
      highlightTimeoutRef.current = setTimeout(() => {
        setHighlightedRequestId(null)
        clearHighlightParam()
      }, cleanupDelay)
    }

    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current)
      }
    }
  }, [requests, isLoading, isInitialLoad, activeTab, timeFilter, filteredRequests, setActiveTab, setTimeFilter, setSearchParams])

  useEffect(() => {
    const handlePopState = () => {
      const highlightParam = getHighlightParam()
      if (!highlightParam && highlightedRequestId) {
        setHighlightedRequestId(null)
      }
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [highlightedRequestId])

  return (
    <ErrorBoundary>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold">Anfragen an mich</h1>
          <button
            onClick={() => fetchRequests(false)}
            disabled={isRefreshing}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition disabled:opacity-50"
            title="Aktualisieren"
          >
            <RefreshCw className={`w-5 h-5 text-gray-600 dark:text-gray-400 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded flex justify-between items-center">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-sm underline">Schliessen</button>
          </div>
        )}

        <div className="mb-4">
          <StornoRequestsPanel onResponded={() => fetchRequests(false)} />
        </div>

        <div className="flex gap-2 mb-3 flex-wrap">
          {STATUS_CHIPS.map(chip => (
            <button
              key={chip.value}
              onClick={() => setActiveTab(chip.value)}
              className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                activeTab === chip.value
                  ? 'bg-blue-500 text-white font-medium'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {chip.label} ({counts[chip.value]})
            </button>
          ))}
        </div>

        <div className="flex gap-2 mb-4">
          {(['current', 'all', 'past'] as TimeFilter[]).map(filter => (
            <button
              key={filter}
              onClick={() => setTimeFilter(filter)}
              className={`px-3 py-1.5 text-sm rounded transition-colors ${
                timeFilter === filter
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {filter === 'all' ? 'Alle' :
               filter === 'current' ? 'Aktuell' : 'Vergangen'}
            </button>
          ))}
          <span className="text-sm text-gray-500 dark:text-gray-400 ml-auto self-center">
            {filteredRequests.length} Anfrage{filteredRequests.length !== 1 ? 'n' : ''}
          </span>
        </div>

        {isInitialLoad && isLoading && (
          <div className="grid grid-cols-1 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border-l-4 border-l-gray-200 dark:border-l-gray-700 p-4 animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-3" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-2" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-4" />
                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded mb-3" />
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
              </div>
            ))}
          </div>
        )}

        {(!isInitialLoad || !isLoading) && (
          <>
            {filteredRequests.length === 0 ? (
              <div className="text-gray-500 dark:text-gray-400 text-center py-16">
                Keine eingehenden Anfragen vorhanden
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {filteredRequests.map(request => (
                  <IncomingRequestCard
                    key={request.id}
                    request={request}
                    onRequestPrep={handleRequestPrep}
                    onCancel={handleCancel}
                    onCancelLesson={handleCancelLesson}
                    onSendMessage={handleSendMessage}
                    onResponded={handleResponded}
                    isHighlighted={highlightedRequestId === request.id}
                    isDimmed={highlightedRequestId !== null && highlightedRequestId !== request.id}
                    onDismissHighlight={handleDismissHighlight}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {stornoTarget && (
        <StornoModal
          isOpen={true}
          onClose={() => { setStornoTarget(null); setStornoPreselectedLessons([]) }}
          requestId={stornoTarget.id}
          initiatorRole="substitute"
          preselectedLessonIds={stornoPreselectedLessons}
          onSuccess={() => {
            setStornoTarget(null)
            setStornoPreselectedLessons([])
            fetchRequests(false)
          }}
        />
      )}
    </ErrorBoundary>
  )
}
